import { createEvent, createEffect, createStore, sample } from 'effector'
import { debounce } from 'patronum'
import * as customerModel from '@entities/customer/model'
import { $isLogging } from '@entities/customer/model'
import { useEvent, useStore, useStoreMap } from 'effector-react'
import { ICatalogGoodBase } from '@entities/goods/contract'
import { Shipment } from 'src/features/shipment-selector/type'
import { parseCookies, setCookie } from 'nookies'
import { getGoodsByIds } from '@api/catalog'
import * as customerApi from '@api/customer/customer'
import { mergeFavorits } from '@api/customer/customer'
import { $device, Device } from 'src/processes/core/device'
import { $goods } from '@entities/goods/model'
import { subscribeLoggerEffect } from 'src/infastruct/effector-utils/subscribe-sentry'
import { $isAuthorization } from '@entities/authorization/model'
import { persist, StorageAdapter } from 'src/shared/lib/effector-storage'

const KEY_STORAGE = 'zdr_favorite-ids'

// export const resetGood = createEvent()
export const initGoodGroup = createEvent<ICatalogGoodBase[]>()

/**
 * load page event
 */
const mountFavoritePage = createEvent()
/**
 * close page event
 */
const unmountFavoritePage = createEvent()

export const $likedGoods = createStore<ICatalogGoodBase[]>([]).on(initGoodGroup, (goods) => goods)
// .reset(resetGood)

/**
 * update goods event
 */
const updateLikedGoods = createEvent<number[]>()

/**
 * update goods effect
 */
const updateGoodsFx = createEffect<
    {
        goodIds: number[]
        state: ICatalogGoodBase[]
    },
    ICatalogGoodBase[]
>({
    handler: async ({ goodIds, state }) => {
        const catalogGoodIds = state?.map((x) => x.webData?.goodId)
        if (catalogGoodIds && catalogGoodIds.length > 0) {
            const isNeedUpdateGoods = goodIds.some((x) => !catalogGoodIds.includes(x))
            if (!isNeedUpdateGoods) {
                return state.filter((x) => goodIds.includes(x.webData.goodId))
            }
        }

        const cookies = parseCookies()
        const shipmentCookieString = cookies['storage-shipment']
        const shipment: Shipment = shipmentCookieString ? JSON.parse(shipmentCookieString) : { cityId: 1 }
        const catalogGoods: ICatalogGoodBase[] = await getGoodsByIds(goodIds, shipment, false)
        return catalogGoods
    }
})

subscribeLoggerEffect(updateGoodsFx)

sample({
    clock: updateLikedGoods,
    source: $likedGoods,
    fn: (source, clock) => ({ goodIds: clock, state: source }),
    target: updateGoodsFx
})

sample({
    clock: updateGoodsFx.doneData,
    source: $device,
    filter: (device) => device === Device.Mobile,
    fn: (_, goods) => goods,
    target: $likedGoods
})

sample({
    clock: updateGoodsFx.doneData,
    source: $device,
    filter: (device) => device === Device.Desktop,
    fn: (_, goods) => goods,
    target: $goods
})

/*
sample({
    clock: mountFavoritePage,
    source: { device: $device, goods: $likedGoods },
    filter: ({ device }) => device === Device.Desktop,
    fn: ({ goods }) => goods,
    target: $goods
})
*/

/*
sample({
    clock: unmountFavoritePage,
    fn: () => [],
    target: $goods
})
*/

const resetFavorite = createEvent()

const toggleFavorite = createEvent<number>()
const addFavorite = createEvent<number>()
const deleteFavorite = createEvent<number>()

export const addFavoriteSaveFx = createEffect<
    {
        goodId: number
    },
    void,
    Error
>(async ({ goodId }) => {
    await customerApi.saveFavorite(goodId)
})
export const deleteFavoriteSaveFx = createEffect<
    {
        goodId: number
    },
    void,
    Error
>(async ({ goodId }) => {
    await customerApi.deleteFavorite(goodId)
})

export const $favorites = createStore<number[]>([], { serialize: 'ignore' }).reset(resetFavorite)

export const pickupFavorite = createEvent()

/*
split({
    source: $favorites,
    clock: toggleFavorite,
    match: {
        add: (favorites, goodId) => !favorites?.some((x) => x === goodId),
        del: (favorites, goodId) => favorites?.some((x) => x === goodId)
    },
    cases: {
        add: addFavorite,
        del: deleteFavorite
    }
})
*/
sample({
    clock: toggleFavorite,
    source: $favorites,
    filter: (source, clock) => !source?.some((x) => x === clock),
    fn: (_, goodId) => goodId,
    target: addFavorite
})

sample({
    clock: toggleFavorite,
    source: $favorites,
    filter: (favorites, goodId) => favorites?.some((x) => x === goodId),
    fn: (_, goodId) => goodId,
    target: deleteFavorite
})

sample({
    clock: addFavorite,
    source: { favorites: $favorites },
    fn: ({ favorites }, clock) => {
        if (!favorites) return [clock]
        return [...favorites, clock]
    },
    target: $favorites
})
sample({
    clock: addFavorite,
    source: $favorites,
    filter: $isAuthorization,
    fn: (_, goodId) => ({ goodId }),
    target: addFavoriteSaveFx
})

sample({
    clock: deleteFavorite,
    source: { favorites: $favorites },
    fn: ({ favorites }, clock) => {
        return favorites.filter((x) => x !== clock)
    },
    target: $favorites
})
sample({
    clock: deleteFavorite,
    source: $favorites,
    filter: $isAuthorization,
    fn: (_, goodId) => ({ goodId }),
    target: deleteFavoriteSaveFx
})

/*const adapter: StorageAdapter = () => {
  return {
    get() {
      const cookies = parseCookies()
      const shipmentStorage = cookies[KEY_STORAGE]
      if (!shipmentStorage) return shipmentStorage
      return JSON.parse(shipmentStorage)
    },

    set(value) {
      setCookie(null, KEY_STORAGE, JSON.stringify(value), {
        maxAge: 365 * 24 * 60 * 60,
        path: '/'
      })
    }
  }
}*/
/*const adapter: StorageAdapter = (key: string) => ({
    get: () => {
        const cookies = parseCookies()
        const shipmentStorage = cookies[KEY_STORAGE]
        if (!shipmentStorage) return shipmentStorage
        return JSON.parse(shipmentStorage) as number[]
    },
    set: (value: number[]) => {
        setCookie(null, KEY_STORAGE, JSON.stringify(value), {
            maxAge: 365 * 24 * 60 * 60,
            path: '/'
        })
    }
})

persist({ store: $favorites, key: KEY_STORAGE, adapter, pickup: pickupFavorite })*/

const adapter: StorageAdapter = () => {
    return {
        async get() {
            const cookies = parseCookies()
            const shipmentStorage = cookies[KEY_STORAGE]
            if (!shipmentStorage) return []
            return JSON.parse(shipmentStorage)
        },

        set(value) {
            setCookie(null, KEY_STORAGE, JSON.stringify(value), {
                maxAge: 365 * 24 * 60 * 60,
                path: '/'
            })
        }
    }
}

persist({ key: KEY_STORAGE, store: $favorites, adapter, pickup: pickupFavorite })

const saveFavoriteBackendFx = createEffect<number[], void>({
    handler: async (values) => {
        await mergeFavorits(values)
    }
})

subscribeLoggerEffect(saveFavoriteBackendFx)

const mergeFavoriteBackendFx = createEffect<number[], number[]>({
    handler: async (values) => await mergeFavorits(values)
})

subscribeLoggerEffect(mergeFavoriteBackendFx)

sample({
    clock: debounce({ source: $favorites, timeout: 1000 }),
    filter: $isLogging,
    target: saveFavoriteBackendFx
})

export const mergeFavorites = createEvent()
const clearFavorite = createEvent()

sample({
    clock: mergeFavorites,
    source: $favorites,
    fn: (source) => source,
    target: mergeFavoriteBackendFx
})

sample({
    clock: clearFavorite,
    fn: () => [],
    target: $favorites
})

sample({
    clock: mergeFavoriteBackendFx.doneData,
    filter: (data) => data && data.length > 0,
    target: $favorites
})

sample({
    clock: customerModel.events.customerProfileReset,
    target: resetFavorite
})

// Favorite page logic

/**
 * is opened page
 */
const $isOpened = createStore<boolean>(false)
    .on(mountFavoritePage, () => true)
    .reset(unmountFavoritePage)

/*sample({
    clock: unmountFavoritePage,
    target: [$likedGoods.reinit!, $goods.reinit!]
})*/

sample({
    clock: $favorites,
    source: $favorites,
    filter: $isOpened,
    target: updateLikedGoods
})

sample({
    clock: mountFavoritePage,
    source: $favorites,
    // filter: $isOpened,
    target: updateLikedGoods
})

export const useMergeFavorites = () => useEvent(mergeFavorites)
export const useClearFavorites = () => useEvent(clearFavorite)
/**
 * toggle favorite
 */
export const useToggleFavorite = () => useEvent(toggleFavorite)

/**
 * add favorite by goodId
 */
export const useAddFavorite = () => useEvent(addFavorite)

/**
 * delete favorite by goodId
 */
export const useDeleteFavorite = () => useEvent(deleteFavorite)

/**
 * mount favorite page
 */
export const useMountFavoritePage = () => useEvent(mountFavoritePage)
/**
 * unmount favorite page
 */
export const useUnmountFavoritePage = () => useEvent(unmountFavoritePage)

/**
 * favorites
 */
export const useFavorites = () => useStore($favorites)
/**
 * some favorite by id
 * @param goodId
 */
export const useSomeFavoriteById = (goodId: number): boolean =>
    useStoreMap({
        store: $favorites,
        keys: [goodId],
        fn: (favorites, [id]): boolean => favorites?.some((x) => x === id) || false
    })

export const useFavoritesCount = () => useStoreMap($favorites, (favorites) => favorites.length)

// export const usePickupFavorite = () => useEvent(pickupFavorite)
